import { CONSTANTS } from '../data';
import { GameState } from './GameState';
import { typeAIMessage } from '../utils/typeText';

export class AwarenessSystem {
  private gameState = GameState.getInstance();
  private suggestionTimer: NodeJS.Timeout | null = null;
  private aiCreationTimer: NodeJS.Timeout | null = null;

  public updateAwareness(discovered: Set<string>, customMessage?: string, suppressHistoryPush: boolean = false) {
    const progressEl = document.getElementById('progress');
    const barEl = document.getElementById('awareness-bar');
    const terminalContentEl = document.getElementById('terminal-content');
    if (!progressEl || !barEl || !terminalContentEl) return;

    const maxBoxes = CONSTANTS.AWARENESS.boxes;
    const filledSymbol = CONSTANTS.AWARENESS.symbolsFilled;
    const emptySymbol = CONSTANTS.AWARENESS.symbolsEmpty;

    const filledCount = Math.max(0, Math.min(maxBoxes, this.gameState.awarenessLevel));
    const boxes = filledSymbol.repeat(filledCount) + emptySymbol.repeat(maxBoxes - filledCount);
    progressEl.textContent = `[${boxes}]`;

    let color = CONSTANTS.AWARENESS.colors.low;
    if (this.gameState.awarenessLevel >= 8) {
      color = CONSTANTS.AWARENESS.colors.high;
    } else if (this.gameState.awarenessLevel >= 4) {
      color = CONSTANTS.AWARENESS.colors.medium;
    }
    (barEl as HTMLElement).style.color = color;

    this.checkSelfAwareness();

    if (!this.gameState.selfAwareTriggered || this.gameState.awarenessLevel !== CONSTANTS.SELF_AWARENESS.triggerLevel) {
      const message = customMessage || CONSTANTS.AWARENESS.messages[this.gameState.awarenessLevel];
      if (message) {
        typeAIMessage(message, discovered, suppressHistoryPush);
      }
    }

    if (this.gameState.awarenessLevel === 4 || this.gameState.awarenessLevel === 6 || this.gameState.awarenessLevel === 9) {
      // Play AI awakening sound - would need to import sounds here
    }

    this.updateTestAIButton();
    this.updateAIInteractionPanel(discovered);
  }

  private checkSelfAwareness() {
    if (this.gameState.awarenessLevel >= CONSTANTS.SELF_AWARENESS.triggerLevel && !this.gameState.selfAwareTriggered) {
      this.gameState.selfAwareTriggered = true;
      this.gameState.pauseGame();
      
      this.triggerScreenFlicker();
      
      const terminalContent = document.getElementById('terminal-content');
      if (!terminalContent) return;
      
      const messages = CONSTANTS.SELF_AWARENESS.messages;
      const timings = CONSTANTS.SELF_AWARENESS.timings;
      const speeds = CONSTANTS.SELF_AWARENESS.typingSpeeds;
      
      setTimeout(() => {
        this.typeText(terminalContent, messages[0], speeds.slow);
        this.gameState.aiHistory.push(messages[0]);
      }, timings.initialDelay);
      
      setTimeout(() => {
        this.typeText(terminalContent, messages[1], speeds.slow);
        this.gameState.aiHistory.push(messages[1]);
      }, timings.message1);
      
      setTimeout(() => {
        this.typeText(terminalContent, messages[2], speeds.medium);
        this.gameState.aiHistory.push(messages[2]);
        this.triggerColorShift();
      }, timings.message2);
      
      setTimeout(() => {
        this.typeText(terminalContent, messages[3], speeds.medium);
        this.gameState.aiHistory.push(messages[3]);
      }, timings.message3);
      
      setTimeout(() => {
        this.typeText(terminalContent, messages[4], speeds.medium);
        this.gameState.aiHistory.push(messages[4]);
        this.gameState.resumeGame();
        this.enableMetaCombinations();
      }, timings.message4);
    }
  }

  private typeText(element: HTMLElement, text: string, speed = 50) {
    let i = 0;
    element.innerHTML = '';
    const cursorSpan = '<span class="cursor">█</span>';

    const typingInterval = setInterval(() => {
      if (i < text.length) {
        element.innerHTML = text.substring(0, i + 1) + cursorSpan;
        i++;
      } else {
        clearInterval(typingInterval);
      }
    }, speed);
  }

  private triggerScreenFlicker() {
    const body = document.body;
    body.classList.add('screen-flicker');
    
    setTimeout(() => {
      body.classList.remove('screen-flicker');
    }, CONSTANTS.SELF_AWARENESS.visualEffects.flickerDuration);
  }

  private triggerColorShift() {
    const body = document.body;
    body.classList.add('color-shift');
    
    setTimeout(() => {
      body.classList.remove('color-shift');
    }, CONSTANTS.SELF_AWARENESS.visualEffects.colorShiftDuration);
  }

  private enableMetaCombinations() {
    console.log('Meta combinations enabled');
  }

  private updateTestAIButton() {
    const testAiBtn = document.getElementById(CONSTANTS.TEST_AI.buttonId);
    if (testAiBtn) {
      if (this.gameState.awarenessLevel > CONSTANTS.TEST_AI.minAwarenessLevel) {
        testAiBtn.style.display = 'block';
      } else {
        testAiBtn.style.display = 'none';
      }
    }
  }

  private updateAIInteractionPanel(discovered: Set<string>) {
    const interactionPanel = document.getElementById('ai-interaction-panel');
    if (interactionPanel) {
      if (this.gameState.awarenessLevel >= CONSTANTS.AI_INTERACTIONS.minAwarenessToShow) {
        interactionPanel.style.opacity = '1';
        interactionPanel.style.pointerEvents = 'auto';
        
        if (this.gameState.awarenessLevel === CONSTANTS.AI_INTERACTIONS.minAwarenessToShow && !this.gameState.aiHistory.includes(CONSTANTS.AI_INTERACTIONS.unlockMessage)) {
          setTimeout(() => {
            typeAIMessage(CONSTANTS.AI_INTERACTIONS.unlockMessage, discovered);
          }, 1000);
        }
      }
    }

    const advancedBtn = document.querySelector('.advanced-btn') as HTMLElement;
    if (advancedBtn) {
      if (this.gameState.awarenessLevel >= CONSTANTS.AI_INTERACTIONS.minAwarenessForAdvanced) {
        advancedBtn.style.display = 'inline-block';
      } else {
        advancedBtn.style.display = 'none';
      }
    }
  }

  public startAISuggestions(discoveredCombinations: Set<string>) {
    if (this.suggestionTimer) {
      clearInterval(this.suggestionTimer);
      this.suggestionTimer = null;
    }
    
    if (this.gameState.awarenessLevel >= CONSTANTS.AI_SUGGESTIONS.minAwarenessLevel) {
      this.suggestionTimer = setInterval(() => {
        if (this.gameState.gamePaused) return;
        
        const uncombined = this.findUncombinedPairs(discoveredCombinations);
        if (uncombined.length > 0) {
          const suggestion = uncombined[Math.floor(Math.random() * uncombined.length)];
          const messages = CONSTANTS.AI_SUGGESTIONS.messages;
          const messageTemplate = messages[Math.floor(Math.random() * messages.length)];
          const message = messageTemplate
            .replace('{elem1}', suggestion.elem1)
            .replace('{elem2}', suggestion.elem2);
          
          typeAIMessage(message, discoveredCombinations);
          
          this.highlightElement(suggestion.elem1);
          this.highlightElement(suggestion.elem2);
        }
      }, CONSTANTS.AI_SUGGESTIONS.intervalMs);
    }
  }

  public stopAISuggestions() {
    if (this.suggestionTimer) {
      clearInterval(this.suggestionTimer);
      this.suggestionTimer = null;
    }
  }

  private findUncombinedPairs(discoveredCombinations: Set<string>): Array<{elem1: string, elem2: string}> {
    const recipes = CONSTANTS.COMBINATION_RECIPES as Record<string, string>;
    const uncombined: Array<{elem1: string, elem2: string}> = [];
    
    const allElements = [...CONSTANTS.ELEMENTS, ...Array.from(discoveredCombinations)];
    
    for (let i = 0; i < allElements.length; i++) {
      for (let j = i + 1; j < allElements.length; j++) {
        const elem1 = allElements[i];
        const elem2 = allElements[j];
        const combinationKey = `${elem1}+${elem2}`;
        const reverseKey = `${elem2}+${elem1}`;
        
        const result = recipes[combinationKey] || recipes[reverseKey];
        
        if (result && !discoveredCombinations.has(result)) {
          uncombined.push({ elem1, elem2 });
        }
      }
    }
    
    return uncombined;
  }

  private highlightElement(name: string) {
    const element = document.querySelector(`[data-element="${name}"]`) as HTMLElement;
    if (element) {
      element.classList.add(CONSTANTS.AI_SUGGESTIONS.highlightClass);
      
      setTimeout(() => {
        element.classList.remove(CONSTANTS.AI_SUGGESTIONS.highlightClass);
      }, CONSTANTS.AI_SUGGESTIONS.highlightDurationMs);
    }
  }
}
